import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CadApplyPlan, useCadApplyPlan } from '../../hooks/useCadApplyPlan';
import { CadApplyField, CadApplyResult, CadRecord, CallDetails } from '../../types/Call';
import { APPLY_NOTHING_ALLOWED, APPLY_SETTINGS_LOADING, visuallyHidden } from './cadRecordDisplay';

interface CadApplyDialogProps {
  callDetails: CallDetails;
  fields: CadRecord;
  /** A plan the caller already resolved for this call and record; otherwise the dialog reads the settings itself. */
  plan?: CadApplyPlan;
  onClose: () => void;
  /** Resolves null when the apply failed (already notified); the dialog then stays open. */
  onApply: (fields: CadApplyField[]) => Promise<CadApplyResult | null>;
  onApplied: (result: CadApplyResult) => void;
}

/**
 * Mounted only while open, so each opening starts from the agency's settings and the call's
 * current values rather than from the last selection.
 */
export const CadApplyDialog: React.FC<CadApplyDialogProps> = ({
  callDetails,
  fields,
  plan: providedPlan,
  onClose,
  onApply,
  onApplied,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const ownPlan = useCadApplyPlan(callDetails, fields, providedPlan === undefined);
  const plan = providedPlan ?? ownPlan;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const preselectedRef = useRef(false);

  // Pre-select once, when the settings arrive. Only once: the call page can hand in a fresh call
  // object while the dialog is open, and that must not undo the supervisor's own selection.
  const rows = plan.status === 'ready' ? plan.rows : [];
  useEffect(() => {
    if (plan.status !== 'ready' || preselectedRef.current) return;
    preselectedRef.current = true;
    setChecked(new Set(plan.rows.filter((row) => row.preselected).map((row) => row.id)));
  }, [plan]);

  const selectedRows = rows.filter((row) => checked.has(row.id) && row.blockedReason === null);
  const operatorRow = selectedRows.find((row) => row.id === 'operator');
  const changesAccess = !!operatorRow?.sendFields.includes('OperatorId');
  const count = selectedRows.length;

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleApply = async () => {
    setSubmitting(true);
    const result = await onApply(selectedRows.flatMap((row) => row.sendFields));
    setSubmitting(false);
    if (result) onApplied(result);
  };

  const title = callDetails.incidentNumber || fields.eventId;

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="cad-apply-title"
      aria-describedby="cad-apply-description"
    >
      <DialogTitle id="cad-apply-title">
        Apply CAD record to call{title ? ` ${title}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography id="cad-apply-description" variant="body2" color="text.secondary" mb={2}>
          Checked fields replace the current value.{' '}
          {plan.status === 'ready' &&
            (plan.policy === 'FillEmpty'
              ? 'Empty or UNKNOWN fields are pre-checked. '
              : 'Every field with a new value is pre-checked. ')}
          Every change is written to the audit trail.
        </Typography>

        {plan.status === 'loading' && (
          <Box display="flex" alignItems="center" gap={1.5} py={2}>
            <CircularProgress size={16} aria-hidden />
            <Typography variant="body2" color="text.secondary">
              {APPLY_SETTINGS_LOADING}
            </Typography>
          </Box>
        )}

        {plan.status === 'error' && <Alert severity="error">{plan.message}</Alert>}

        {plan.status === 'ready' && rows.length === 0 && (
          <Alert severity="info">{APPLY_NOTHING_ALLOWED}</Alert>
        )}

        {rows.length > 0 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: fullScreen ? undefined : 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Box component="span" sx={visuallyHidden}>
                      Apply
                    </Box>
                  </TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>From CAD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const labelId = `cad-apply-row-${row.id}`;
                  const blocked = row.blockedReason !== null;
                  const reasonId = `${labelId}-reason`;
                  return (
                    <TableRow key={row.id} hover={!blocked}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked.has(row.id) && !blocked}
                          disabled={blocked || submitting}
                          onChange={() => toggle(row.id)}
                          inputProps={{
                            'aria-labelledby': labelId,
                            'aria-describedby': blocked ? reasonId : undefined,
                          }}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row" id={labelId}>
                        {row.label}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        <ValueText value={row.currentValue} emptyText="Not set" />
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        {row.cadValue && (
                          <Typography variant="body2" fontWeight={500}>
                            {row.cadValue}
                          </Typography>
                        )}
                        {row.blockedReason && (
                          <Typography
                            id={reasonId}
                            variant="caption"
                            color="text.secondary"
                            fontStyle="italic"
                          >
                            {row.blockedReason}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {changesAccess && operatorRow && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Setting the operator changes who can see this call.</strong> An Agent whose
            operator alias is {operatorRow.cadValue} will be able to open it
            {callDetails.operatorId
              ? `, and the Agent with alias ${callDetails.operatorId} will no longer`
              : ''}
            . If your access is limited to a team, you may lose access to this call. This is
            recorded as an access change.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={count === 0 || submitting}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" aria-hidden /> : undefined
          }
        >
          {count === 0 ? 'Apply' : `Apply ${count} ${count === 1 ? 'field' : 'fields'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ValueText: React.FC<{ value: string | undefined; emptyText: string }> = ({
  value,
  emptyText,
}) =>
  value ? (
    <Typography variant="body2">{value}</Typography>
  ) : (
    <Typography variant="body2" color="text.secondary" fontStyle="italic">
      {emptyText}
    </Typography>
  );
